import { useState } from 'react'
import { logger } from '../../utils/logger'
import { productApi } from '../../services/api'

interface QuantityEditorProps {
  productId: string
  initialQuantity: number
  productStatus: 'active' | 'draft' | 'archived'
  onUpdate?: (newQuantity: number, newStatus?: string) => void
  onError?: (error: string) => void
}

export default function QuantityEditor({
  productId,
  initialQuantity,
  productStatus,
  onUpdate,
  onError,
}: QuantityEditorProps) {
  const [quantity, setQuantity] = useState(initialQuantity)
  const [isUpdating, setIsUpdating] = useState(false)

  const updateQuantity = async (newQuantity: number) => {
    setIsUpdating(true)
    try {
      // Update quantity using productApi
      await productApi.updateProductQuantity(productId, newQuantity)
      setQuantity(newQuantity)

      // If quantity becomes 0, deactivate and archive the product
      if (newQuantity === 0 && productStatus !== 'archived') {
        logger.info('Quantity is 0, deactivating and archiving product', { productId })

        // First deactivate if active
        if (productStatus === 'active') {
          await productApi.deactivateProduct(productId)
          logger.info('Product deactivated due to 0 quantity', { productId })
        }

        // Then archive
        const archivedProduct = await productApi.archiveProduct(productId)
        logger.info('Product archived due to 0 quantity', { productId })
        onUpdate?.(newQuantity, archivedProduct.status)
        return
      }

      // If quantity increases from 0 for archived product, unarchive to draft (stays deactivated)
      if (newQuantity > 0 && productStatus === 'archived') {
        logger.info('Quantity increased from 0, unarchiving product to draft', { productId })
        const draftProduct = await productApi.deactivateProduct(productId)
        logger.info('Archived product unarchived to draft', { productId })
        onUpdate?.(newQuantity, draftProduct.status)
        return
      }

      logger.info('Quantity updated successfully', { productId, quantity: newQuantity })
      onUpdate?.(newQuantity)
    } catch (error) {
      logger.error('Failed to update quantity', error)
      const errorMessage = error instanceof Error ? error.message : 'Failed to update quantity'
      onError?.(errorMessage)
      // Revert to previous quantity on error
      setQuantity(quantity)
    } finally {
      setIsUpdating(false)
    }
  }

  const handleIncrement = () => {
    const newQuantity = quantity + 1
    updateQuantity(newQuantity)
  }

  const handleDecrement = () => {
    const newQuantity = Math.max(0, quantity - 1)
    updateQuantity(newQuantity)
  }

  return (
    <div className="flex items-center space-x-2">
      <div className="flex items-center space-x-1">
        <span className="text-sm text-gray-600">Stock:</span>
        <span className={`text-sm font-semibold ${quantity === 0 ? 'text-red-600' : 'text-gray-800'}`}>
          {quantity}
        </span>
        {quantity === 0 && (
          <span className="text-xs bg-red-100 text-red-800 px-2 py-0.5 rounded-full ml-1">
            Out of stock
          </span>
        )}
      </div>
      <div className="flex items-center border border-gray-300 rounded-lg overflow-hidden">
        {quantity > 0 && (
          <button
            onClick={handleDecrement}
            disabled={isUpdating}
            className="w-8 h-8 bg-gray-100 active:bg-gray-200 text-gray-800 font-bold text-lg flex items-center justify-center disabled:opacity-50 touch-manipulation"
          >
            −
          </button>
        )}
        <button
          onClick={handleIncrement}
          disabled={isUpdating}
          className="w-8 h-8 bg-gray-100 active:bg-gray-200 text-gray-800 font-bold text-lg flex items-center justify-center disabled:opacity-50 touch-manipulation"
        >
          +
        </button>
      </div>
    </div>
  )
}
